import { VehiclePositionState } from '../../types/busHistoryTypes';
import { ActionType, BusPositionHistoryAction } from './types/ActionType';
import { AgencyModel } from './types/agencyModel';
import { BphDirectionsState, BphRoutesState, BphSliderState, DateTimeState, StopPredictionsState } from './types/bphStates';
import { BphDirection,
    BphStopsStateType,
    BphUrlFiltersState,
    BphVehicleModel,
    BusPositionHistoryStateType,
    StopsSearchStateType } from './types/BphTypes';
import { DateTime } from './types/dateTime';
import { SliderInfoProps as BphSliderInfoProps } from './types/SliderInfoProps';

export function bphReducer(state: BusPositionHistoryStateType, action: BusPositionHistoryAction): BusPositionHistoryStateType {
    const handler = (handlers as any)[action.type] || ((s: BusPositionHistoryStateType) => s);
    return handler(state, action);
}

export type ActionMap = {
    [actionType in ActionType]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => BusPositionHistoryStateType
};

export const handlers: Partial<ActionMap> = {
    [ActionType.SET_MAP_READY]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => ({
        ...state,
        mapReady: !!action.payload,
    }),
    [ActionType.SET_AGENCY]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => ({
        ...state,
        agency: action.payload as (AgencyModel | null),
    }),
    [ActionType.SET_DATE_TIME]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as DateTimeState;
        if (state.dateTime.selectedDateTime === payload.selectedDateTime) return state;
        return {
            ...state,
            dateTime: payload,
        };
    },
    [ActionType.SET_ROUTES_LOADING]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload === true;
        if (state.routesLoading === payload) return state;
        return {
            ...state,
            routesLoading: payload,
        };
    },
    [ActionType.SET_ROUTES_STATE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as BphRoutesState;
        if (state.routesState === payload) return state;
        return {
            ...state,
            routesState: payload,
        };
    },
    [ActionType.SET_STOPS_PREDICTIONS]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as Nullable<StopPredictionsState>;
        if (state.stopsPredictions === payload) return state;
        return {
            ...state,
            stopsPredictions: payload,
        };
    },
    [ActionType.SET_DIRECTIONS_STATE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as BphDirectionsState;
        if (state.directionsState === payload) return state;
        return {
            ...state,
            directionsState: payload,
        };
    },
    [ActionType.SET_STOPS_LOADING]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload === true;
        if (state.stopsLoading === payload) return state;
        return {
            ...state,
            stopsLoading: payload,
        };
    },
    [ActionType.SET_STOPS_STATE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as BphStopsStateType;
        if (state.stopsState === payload) return state;
        return {
            ...state,
            stopsState: payload,
        };
    },
    [ActionType.SET_STOP_SEARCH_STATE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as StopsSearchStateType;
        if (state.stopsSearchState.searchQuery === payload.searchQuery) return state;
        return {
            ...state,
            stopsSearchState: payload,
        };
    },
    [ActionType.SET_NO_STOPS_MESSSAGE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as string;
        if (state.stopsNoResultMessageState === payload) return state;
        return {
            ...state,
            stopsNoResultMessageState: payload,
        };
    },
    [ActionType.SET_DIRECTIONS_LOADING]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload === true;
        if (state.directionsLoading === payload) return state;
        return {
            ...state,
            directionsLoading: payload,
        };
    },
    [ActionType.SET_DIRECTIONS_INFO]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as BphDirection[];
        if (state.directionsInfo === payload) return state;
        return {
            ...state,
            directionsInfo: payload,
        };
    },
    [ActionType.SET_HISTORY_LOADING]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload === true;
        if (state.historyLoading === payload) return state;
        return {
            ...state,
            historyLoading: payload,
        };
    },
    [ActionType.SET_BUS_POSITIONS]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as VehiclePositionState[];
        if (state.busPositionsState === payload) return state;
        return {
            ...state,
            busPositionsState: payload,
        };
    },
    [ActionType.SET_SCHEDULE_DATES]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as Nullable<DateTime[]>;
        if (state.scheduleDates === payload) return state;
        return {
            ...state,
            scheduleDates: payload,
        };
    },
    [ActionType.SET_SLIDER_OPTIONS]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as Nullable<BphSliderState>;
        if (state.sliderOptions === payload) return state;
        return {
            ...state,
            sliderOptions: payload,
        };
    },
    [ActionType.SET_SLIDER_INFO]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as Nullable<BphSliderInfoProps>;
        if (state.sliderInfo === payload) return state;
        return {
            ...state,
            sliderInfo: payload,
        };
    },
    [ActionType.DISPLY_DIRECTION_STOPS]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as boolean;
        if (state.displayDirectionStops === payload) return state;
        return {
            ...state,
            displayDirectionStops: payload,
        };
    },
    [ActionType.SET_MAP_BOUNDS]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as any;
        if (state.mapsBounds === payload) return state;
        return {
            ...state,
            mapsBounds: payload,
        };
    },
    [ActionType.SET_VEHICLES]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as Nullable<BphVehicleModel[]>;
        if (state.vehicles === payload) return state;
        return {
            ...state,
            vehicles: payload,
        };
    },
    [ActionType.SET_URL_FILTERS_STATE]: (state: BusPositionHistoryStateType, action: BusPositionHistoryAction) => {
        const payload = action.payload as BphUrlFiltersState;
        if (state.urlFiltersState === payload) return state;
        return {
            ...state,
            urlFiltersState: payload,
        };
    },
};
